import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LIBRARY_CHANGED, libraryDocName, type Library as LibraryData, type User } from "@quill/shared";
import * as Y from "yjs";
import { clearDocument } from "y-indexeddb";
import { NetworkError, api, setUnauthorizedHandler } from "./api";
import { cache } from "./cache";
import { LibraryView } from "./library";
import { launchSetting } from "./launch";
import { navigate, useRoute } from "./router";
import { Login } from "./screens/Login";
import { Library } from "./screens/Library";
import { EditorScreen } from "./screens/Editor";
import { Trash } from "./screens/Trash";
import { openDictionary } from "./spell/checker";
import { openProgress } from "./progress";
import { SyncClient, docStoreName } from "./sync";

const collabUrl = () => `${location.protocol === "https:" ? "wss" : "ws"}://${location.host}/collab`;

export function App() {
  // Start from the cached user so the app opens instantly and offline.
  const [user, setUser] = useState<User | null | undefined>(() => cache.user() ?? undefined);

  const signOut = useCallback((callServer: boolean) => {
    if (callServer) void api.logout().catch(() => undefined);
    cache.setUser(null);
    setUser(null);
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(() => signOut(false));
    api
      .me()
      .then((u) => {
        cache.setUser(u);
        setUser(u);
      })
      .catch((err) => {
        // Offline: keep the cached user. Otherwise (401) the handler above signed out.
        if (err instanceof NetworkError) setUser((u) => u ?? null);
      });
  }, [signOut]);

  if (user === undefined) return null;
  if (user === null)
    return (
      <Login
        onLogin={(u) => {
          cache.setUser(u);
          setUser(u);
        }}
      />
    );
  return <SignedIn key={user.id} user={user} onSignOut={() => signOut(true)} />;
}

function SignedIn({ user, onSignOut }: { user: User; onSignOut(): void }) {
  const [sync, setSync] = useState<SyncClient | null>(null);
  useEffect(() => {
    const s = new SyncClient(collabUrl(), user.id);
    setSync(s);
    return () => s.destroy();
  }, [user.id]);
  useEffect(() => openDictionary(user.id), [user.id]);
  useEffect(() => openProgress(user.id), [user.id]);
  return sync ? <Screens user={user} sync={sync} onSignOut={onSignOut} /> : null;
}

/**
 * "Open where she left off" happens once per app launch (a browser session), and only
 * when launched to the library. Reloading the tab doesn't jump away again. Settings can
 * turn it off on this device (launch.ts).
 */
const LAUNCHED_KEY = "quill.launched";
const launchHandled = () => {
  try {
    if (sessionStorage.getItem(LAUNCHED_KEY)) return true;
    sessionStorage.setItem(LAUNCHED_KEY, "1");
  } catch {
    // Storage blocked: treat every load as a launch.
  }
  return false;
};

function Screens({ user, sync, onSignOut }: { user: User; sync: SyncClient; onSignOut(): void }) {
  const route = useRoute();
  const [library, setLibrary] = useState<LibraryData | null>(() => cache.library(user.id));
  const view = useMemo(() => (library ? new LibraryView(library) : null), [library]);

  const latest = useRef(library);
  // True once the first fetch has answered (or failed, e.g. offline).
  const [fetched, setFetched] = useState(false);
  const refresh = useCallback(() => {
    api
      .library()
      .then((lib) => {
        if (latest.current) forgetPurged(latest.current, lib, user.id, sync);
        latest.current = lib;
        cache.setLibrary(user.id, lib);
        setLibrary(lib);
      })
      .catch(() => undefined)
      .finally(() => setFetched(true));
  }, [user.id, sync]);

  useEffect(() => {
    refresh();
    // Library changes from other devices arrive as a broadcast on this channel.
    const channel = new Y.Doc();
    const detach = sync.attach(libraryDocName(user.id), channel, {
      onStateless: (payload) => payload === LIBRARY_CHANGED && refresh(),
    });
    window.addEventListener("online", refresh);
    return () => {
      detach();
      channel.destroy();
      window.removeEventListener("online", refresh);
    };
  }, [sync, user.id, refresh]);

  // Leaving a document: refetch so the library shows its new word count.
  const wasEditor = useRef(route.name === "editor");
  useEffect(() => {
    if (wasEditor.current && route.name !== "editor") refresh();
    wasEditor.current = route.name === "editor";
  }, [route.name, refresh]);

  useEffect(() => {
    // Decide on fresh data where possible: the last edit may have been on another device.
    if (!fetched || launchHandled()) return;
    if (launchSetting() !== "last") return;
    if (route.name !== "library" || route.folderId !== null) return;
    const last = view?.lastEdited();
    // Keep the library underneath so Back leads there.
    if (last) navigate(`/d/${last.id}`);
  }, [fetched, view, route]);

  if (route.name === "editor") {
    const meta = library?.documents.find((d) => d.id === route.id);
    return (
      <EditorScreen
        sync={sync}
        userId={user.id}
        docId={route.id}
        meta={meta}
        trashed={!!(meta && view?.docTrashed(meta))}
      />
    );
  }
  if (route.name === "trash") return <Trash view={view} refresh={refresh} />;
  return <Library view={view} folderId={route.folderId} refresh={refresh} onSignOut={onSignOut} />;
}

/**
 * Documents that were in the previous listing and are gone now were removed by Empty
 * Trash. Drop their local copies, unless they hold edits the server never received.
 */
function forgetPurged(prev: LibraryData, next: LibraryData, userId: string, sync: SyncClient) {
  const still = new Set(next.documents.map((d) => d.id));
  const pending = new Set(sync.pending());
  for (const d of prev.documents) {
    if (!still.has(d.id) && d.deletedAt && !pending.has(d.id)) void clearDocument(docStoreName(userId, d.id));
  }
}
